import React, { useState } from 'react'
import styled from '@emotion/styled'
import { useAuth } from '../../contexts/AuthContext'

const Container = styled.div`
  max-width: 400px;
  margin: 0 auto;
  padding: 40px 20px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
`

const Title = styled.h2`
  text-align: center;
  color: var(--color-text);
  margin-bottom: 32px;
  font-family: var(--font-heading);
  font-size: 32px;
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.02em;
  background: linear-gradient(
    135deg,
    var(--color-text) 0%,
    var(--color-primary) 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`

const Subtitle = styled.p`
  text-align: center;
  color: var(--color-text-secondary);
  margin-bottom: 32px;
  font-size: 14px;
  font-family: var(--font-body);
`

const SocialButton = styled.button`
  width: 100%;
  padding: 14px 24px;
  background: var(--color-surface);
  color: var(--color-text);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  font-size: 16px;
  font-weight: 600;
  font-family: var(--font-body);
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 12px;

  &:hover:not(:disabled) {
    background: var(--color-background);
    border-color: var(--color-primary);
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: 20px 0;
  
  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--color-border);
  }
  
  span {
    padding: 0 12px;
    color: var(--color-text-secondary);
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
`

const ErrorMessage = styled.div`
  color: var(--color-error);
  font-size: 14px;
  margin-top: 16px;
  padding: 8px 12px;
  background: var(--color-error-light, rgba(239, 68, 68, 0.1));
  border: 1px solid rgba(255, 107, 107, 0.2);
  border-radius: var(--radius-sm);
  font-family: var(--font-body);
  text-align: center;
`

export const LoginForm: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  const { signInWithGoogle, signInWithDiscord } = useAuth()

  const handleGoogleSignIn = async () => {
    console.log('Google sign-in button clicked');
    console.log('Current URL:', window.location.href);
    console.log('User Agent:', navigator.userAgent);
    
    setError(null)
    setLoading(true)
    
    try {
      await signInWithGoogle()
      console.log('signInWithGoogle completed');
      // Don't set loading to false here - let the auth redirect handle it
    } catch (err: any) {
      console.error('Google sign-in error:', err);
      console.error('Error details:', {
        message: err.message,
        name: err.name,
        stack: err.stack
      });
      
      // Provide more helpful error messages
      let errorMessage = err.message;
      if (err.message?.includes('popup')) {
        errorMessage = 'Pop-up was blocked. Please allow pop-ups for this site.';
      } else if (err.message?.includes('network')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      setError(errorMessage)
      setLoading(false)
    }
  }

  const handleDiscordSignIn = async () => {
    console.log('Discord sign-in button clicked');
    
    setError(null)
    setLoading(true)
    
    try {
      await signInWithDiscord()
      console.log('signInWithDiscord completed');
    } catch (err: any) {
      console.error('Discord sign-in error:', err);
      setError(err.message || 'Failed to sign in with Discord')
      setLoading(false)
    }
  }

  return (
    <Container>
      <Title>Lore Spirals</Title>
      <Subtitle>Sign in to explore and save your discoveries</Subtitle>
      
      <SocialButton onClick={handleGoogleSignIn} disabled={loading}>
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
          <path fillRule="evenodd" clipRule="evenodd" d="M17.64 9.20454C17.64 8.56636 17.5827 7.95272 17.4764 7.36363H9V10.845H13.8436C13.635 11.97 13.0009 12.9231 12.0477 13.5613V15.8195H14.9564C16.6582 14.2527 17.64 11.9454 17.64 9.20454Z" fill="#4285F4"/>
          <path fillRule="evenodd" clipRule="evenodd" d="M9 18C11.43 18 13.4673 17.1941 14.9564 15.8195L12.0477 13.5613C11.2418 14.1013 10.2109 14.4204 9 14.4204C6.65591 14.4204 4.67182 12.8372 3.96409 10.71H0.957275V13.0418C2.43818 15.9831 5.48182 18 9 18Z" fill="#34A853"/>
          <path fillRule="evenodd" clipRule="evenodd" d="M3.96409 10.71C3.78409 10.17 3.68182 9.59318 3.68182 9C3.68182 8.40682 3.78409 7.83 3.96409 7.29V4.95818H0.957273C0.347727 6.17318 0 7.54773 0 9C0 10.4523 0.347727 11.8268 0.957273 13.0418L3.96409 10.71Z" fill="#FBBC05"/>
          <path fillRule="evenodd" clipRule="evenodd" d="M9 3.57955C10.3214 3.57955 11.5077 4.03364 12.4405 4.92545L15.0218 2.34409C13.4632 0.891818 11.4259 0 9 0C5.48182 0 2.43818 2.01682 0.957275 4.95818L3.96409 7.29C4.67182 5.16273 6.65591 3.57955 9 3.57955Z" fill="#EA4335"/>
        </svg>
        {loading ? 'Signing in...' : 'Sign in with Google'}
      </SocialButton>
      
      <SocialButton onClick={handleDiscordSignIn} disabled={loading}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M20.317 4.3698C18.7873 3.66448 17.147 3.14734 15.4319 2.84871C15.4007 2.84292 15.3695 2.85741 15.3534 2.88637C15.1424 3.2614 14.9087 3.74988 14.7451 4.13367C12.9004 3.85737 11.0652 3.85737 9.25832 4.13367C9.09465 3.74141 8.85248 3.2614 8.64057 2.88637C8.62449 2.85838 8.59328 2.8439 8.56205 2.84871C6.84791 3.14637 5.20756 3.66351 3.67693 4.3698C3.66368 4.37557 3.65233 4.38521 3.64479 4.39773C0.533392 9.04636 -0.31895 13.5816 0.0991801 18.0606C0.101072 18.0825 0.11337 18.1034 0.130398 18.1176C2.18321 19.6206 4.17171 20.5303 6.12328 21.1303C6.15451 21.1399 6.18761 21.1283 6.20748 21.1015C6.66913 20.471 7.08064 19.8072 7.43348 19.111C7.4543 19.0709 7.43442 19.0232 7.39186 19.0078C6.73913 18.7595 6.1176 18.4567 5.51973 18.1101C5.47244 18.0832 5.46865 18.0153 5.51216 17.9836C5.63797 17.8893 5.76382 17.7911 5.88396 17.6919C5.90569 17.6738 5.93598 17.67 5.96153 17.6813C9.88928 19.4834 14.1415 19.4834 18.023 17.6813C18.0485 17.6691 18.0788 17.6729 18.1015 17.691C18.2216 17.7902 18.3475 17.8893 18.4742 17.9836C18.5177 18.0153 18.5149 18.0832 18.4676 18.1101C17.8697 18.4634 17.2482 18.7595 16.5945 19.0069C16.552 19.0222 16.533 19.0709 16.5538 19.111C16.9143 19.8062 17.3258 20.47 17.7789 21.1005C17.7978 21.1283 17.8319 21.1399 17.8631 21.1303C19.8241 20.5303 21.8126 19.6206 23.8654 18.1176C23.8834 18.1034 23.8948 18.0835 23.8967 18.0616C24.3971 12.8783 23.0585 8.38104 20.3482 4.39871C20.3416 4.38521 20.3303 4.37557 20.317 4.3698ZM8.02002 15.3312C6.8375 15.3312 5.86313 14.2559 5.86313 12.9392C5.86313 11.6226 6.8186 10.5473 8.02002 10.5473C9.23087 10.5473 10.1958 11.6323 10.1769 12.9392C10.1769 14.2559 9.22141 15.3312 8.02002 15.3312ZM15.9947 15.3312C14.8123 15.3312 13.8379 14.2559 13.8379 12.9392C13.8379 11.6226 14.7933 10.5473 15.9947 10.5473C17.2056 10.5473 18.1705 11.6323 18.1516 12.9392C18.1516 14.2559 17.2056 15.3312 15.9947 15.3312Z" fill="#5865F2"/>
        </svg>
        {loading ? 'Signing in...' : 'Sign in with Discord'}
      </SocialButton>

      {error && <ErrorMessage>{error}</ErrorMessage>}
    </Container>
  )
}